<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { ElButton } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { DateTime } from 'luxon'
import type { GeoFenceVisitWithObjectsLinked } from '~/services/apiClientGps'
import CompletedIcon from '~/components/icons/CompletedIcon.vue'
import InProgressIcon from '~/components/icons/InProgressIcon.vue'
import TruckIcon from '~/components/icons/TruckIcon.vue'
import InGateIcon from '~/components/icons/InGateIcon.vue'
import OutGateIcon from '~/components/icons/OutGateIcon.vue'
import { calculateDuration, formatTime } from '~/utils/dateTimeUtils'

defineProps<{
  visit: GeoFenceVisitWithObjectsLinked
  isSelected: boolean
  index: number
  totalVisits: number
}>()

const emit = defineEmits<{
  (e: 'select', visit: GeoFenceVisitWithObjectsLinked): void
}>()

// Constants
const DURATION_THRESHOLDS_HOURS = {
  NORMAL_MAX: 1.5, // Up to 1.5 hours is normal
  WARNING_MAX: 2, // Up to 2 hours is a warning
  // Anything above WARNING_MAX would be critical
} as const

const DURATION_COLOR_CLASSES = {
  NORMAL: 'text-gray-500',
  WARNING: 'text-orange-500',
  CRITICAL: 'text-red-500',
} as const

// Function to get duration color based on visit duration
const getDurationColor = (visit: GeoFenceVisitWithObjectsLinked): string => {
  if (!visit.arrival) return 'text-gray-500'

  const arrivalTime = new Date(visit.arrival).getTime()
  const endTime = visit.departure
    ? new Date(visit.departure).getTime()
    : Date.now()

  const durationMs = endTime - arrivalTime
  const durationHours = durationMs / (1000 * 60 * 60) // Convert to hours

  if (durationHours < DURATION_THRESHOLDS_HOURS.NORMAL_MAX) {
    return DURATION_COLOR_CLASSES.NORMAL
  } else if (durationHours < DURATION_THRESHOLDS_HOURS.WARNING_MAX) {
    return DURATION_COLOR_CLASSES.WARNING
  } else {
    return DURATION_COLOR_CLASSES.CRITICAL
  }
}
</script>

<template>
  <div
    class="cursor-pointer hover:bg-gray-50 transition-colors"
    :class="[
      {
        'bg-blue-100 hover:bg-blue-100': isSelected,
      },
      { 'border-b border-gray-100': index !== totalVisits - 1 },
    ]"
    @click="emit('select', visit)"
  >
    <div class="p-3">
      <!-- First Row -->
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-2">
          <span class="text-gray-700 font-medium">
            {{ visit.geo_fence?.location?.name?.toUpperCase() || '---' }}
          </span>
          <span class="text-sm text-gray-400">•</span>
          <div class="flex items-center space-x-2">
            <span class="text-lg font-medium text-gray-900">
              <ClickToCopy :text-to-copy="visit.tracking_device?.external_id">
                {{ visit.tracking_device?.name }}
              </ClickToCopy>
            </span>
            <el-tag
              v-if="visit.tracking_device?.drivers?.[0]?.name"
              size="small"
              type="info"
              effect="plain"
            >
              <div class="flex items-center space-x-1">
                <i-mdi:account class="w-3 h-3" />
                <span
                  class="truncate max-w-[120px]"
                  :title="visit.tracking_device?.drivers?.[0]?.name"
                >
                  {{ visit.tracking_device?.drivers?.[0]?.name }}
                </span>
              </div>
            </el-tag>
          </div>
        </div>
        <div class="flex items-center space-x-2">
          <div class="flex items-center space-x-1">
            <span
              class="text-sm font-medium"
              :class="visit.departure ? 'text-gray-900' : 'text-blue-600'"
            >
              {{ visit.departure ? 'Completed' : 'In Progress' }}
            </span>
            <component
              :is="visit.departure ? CompletedIcon : InProgressIcon"
              class="w-4 h-4"
              :class="visit.departure ? 'text-green-600' : 'text-blue-600'"
            />
          </div>
        </div>
      </div>

      <!-- Second Row -->
      <div class="flex items-center justify-between mt-2">
        <div class="flex items-center space-x-4">
          <div class="flex items-center space-x-3 text-sm text-gray-600">
            <span
              >Date: {{ DateTime.fromISO(visit.arrival).toFormat('M/d') }}</span
            >
            <ElButton plain type="primary" size="small" :icon="Plus" disabled>
              Appt
            </ElButton>
          </div>
        </div>
        <div class="flex items-center space-x-4 text-sm">
          <div class="flex items-center space-x-3 text-gray-600">
            <div v-tooltip="'Line Time'" class="flex items-center space-x-1">
              <TruckIcon class="w-4 h-4" />
              <span>{{ formatTime(visit.line) }}</span>
            </div>
            <div v-tooltip="'Entry Time'" class="flex items-center space-x-1">
              <InGateIcon class="w-4 h-4" />
              <span>{{ formatTime(visit.arrival) }}</span>
            </div>
            <div v-tooltip="'Exit Time'" class="flex items-center space-x-1">
              <OutGateIcon class="w-4 h-4" />
              <span>{{ formatTime(visit.departure) }}</span>
            </div>
            <div
              class="flex items-center font-medium"
              :class="getDurationColor(visit)"
            >
              <i-mdi:timer-outline class="w-4 h-4 mr-1" />
              {{ calculateDuration(visit.arrival, visit.departure) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
